import React from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import LoadingSpinner from '../common/LoadingSpinner';

const FormSelect = ({ label, options, value, onChange, placeholder, required, disabled, loading, className, isMulti, optionRender, styles }) => {
  // Custom Option component to support optionRender
  const CustomOption = props => {
    if (optionRender) {
      return (
        <components.Option {...props}>
          {optionRender(props.data)}
        </components.Option>
      );
    }
    return <components.Option {...props} />;
  };

  // Show loading option if loading
  const loadingOptions = loading ? [{ value: 'loading', label: 'Loading...', isDisabled: true }] : options;

  return (
    <div className="space-y-1">
      {label && (
        <label className="font-medium text-gray-700 flex items-center">
          {label}
          {required && <span className="ml-1 text-darkred text-sm animate-pulse">*</span>}
        </label>
      )}
      <div className="relative">
        <Select
          options={loadingOptions}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isDisabled={disabled || loading}
          className={className}
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          components={{ Option: CustomOption }}
          styles={{
            ...styles,
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            menu: base => ({
              ...base,
              zIndex: 9999,
              maxHeight: '200px', // Set max height for scrollable menu
              overflowY: 'auto', // Enable vertical scrolling
            }),
            menuList: base => ({
              ...base,
              maxHeight: '200px', // Ensure menuList respects max height
              overflowY: 'auto', // Enable scrolling for menu items
              padding: '4px 0', // Add padding for better appearance
            }),
            multiValue: base => ({
              ...base,
              backgroundColor: '#e9d8fd',
              borderRadius: '0.375rem',
              padding: '2px 4px',
            }),
            multiValueLabel: base => ({
              ...base,
              color: '#5b21b6',
              fontSize: '0.875rem',
            }),
            multiValueRemove: base => ({
              ...base,
              backgroundColor: '#dc2626',
              color: 'white',
              ':hover': {
                backgroundColor: '#991b1b',
              },
            }),
          }}
        />
        {loading && !options.length && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>
  );
};

FormSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      color: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      color: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        color: PropTypes.string,
      })
    ),
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  isMulti: PropTypes.bool,
  optionRender: PropTypes.func,
  styles: PropTypes.object,
};

FormSelect.defaultProps = {
  isMulti: false,
  optionRender: null,
  styles: {},
};

export default FormSelect;